import { useRef, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { type MenuItem } from '@/types/common';
import { type PopoverAlignment } from '@/utils/popoverPosition';
import IcHamburger from '@/assets/icons/Hamburger.svg?react';
import NavigationLink from './NavigationLink';
import styles from './Dropdown.module.scss';

interface DropdownProps {
  align?: 'left' | 'right';
  className?: { hamburger?: string; content?: string };
  level?: number;
  menuItems: MenuItem[];
  popover?: PopoverAlignment;
  ref?: Ref<HTMLDivElement>;
}

const Dropdown = ({
  align,
  className,
  level = 0,
  menuItems,
  popover = 'center bottom',
  ref,
}: DropdownProps) => {
  const { i18n, t } = useTranslation(['common']);
  const currentPopoverRef = useRef<HTMLDivElement>(null);
  const lang = i18n.language ?? 'en';

  const handleClick = () => {
    if (ref && typeof ref !== 'function' && 'current' in ref && ref.current) {
      ref.current.hidePopover();
    }
    currentPopoverRef.current?.hidePopover();
  };

  const children = menuItems.map((item) => {
    const href = item.href
      .replace('{lang}', lang === 'en' ? '' : lang)
      .replace(/([^:]\/)\/+/g, '$1'); // In some cases "//" is when the language shouldn't be part of the URL

    return (
      <li key={item.label + item.href}>
        <NavigationLink
          action={item.action}
          align={align}
          hasSubmenu={!!item.submenuItems?.length}
          highlight={item.highlight}
          href={href}
          target={item.target}
          Icon={item.icon}
          label={item.label}
          refreshOnSameClick={item.refreshOnSameClick}
          onClick={handleClick}
        />
        {item.submenuItems?.length && (
          <Dropdown
            menuItems={item.submenuItems}
            level={level + 1}
            ref={ref ?? currentPopoverRef}
          />
        )}
      </li>
    );
  });

  if (level > 0) {
    return <ul className={styles.menu}>{children}</ul>;
  }

  return (
    <>
      <button
        type="button"
        className={classNames(styles.hamburger, className?.hamburger)}
        aria-label={t('common:menu.hamburgerButton')}
        popoverTarget="dropdown-menu"
        id="hamburger"
      >
        <IcHamburger className={styles.icon} />
      </button>
      <div
        className={classNames(styles.content, className?.content)}
        popover="auto"
        id="dropdown-menu"
        ref={currentPopoverRef}
        data-anchor="hamburger"
        data-position={popover}
        data-reposition
      >
        <ul className={styles.menu}>{children}</ul>
      </div>
    </>
  );
};

export default Dropdown;
